import { FC, FocusEventHandler, MouseEvent, ReactNode, useState } from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { CloseOutlined as Close } from '@mui/icons-material';

import { CustomizedInput } from '../Input';
import Text from '../Text';
import { ORANGE, RED, TEXT } from '../../Helpers/colors';
import { ChevronDown } from '../../Assets/svgIcons';

export interface SelectDataProperties {
	id: string | number;
	isDefault?: boolean;
	isDisabled?: boolean;
	label?: string;
	name?: string;
	text?: string;
}

interface SelectProperties {
	className?: string;
	data: SelectDataProperties[] | null;
	description?: string;
	disabled?: boolean;
	error?: boolean;
	errorMessage?: string;
	label?: string;
	onBlur?: FocusEventHandler<HTMLInputElement>;
	onConfirm?: () => void;
	placeholder?: string;
	setValue: (event: SelectChangeEvent<string>) => void;
	startAdornment?: ReactNode;
	value: string;
	warning?: boolean;
	warningMessage?: string;
}

const CustomSelect: FC<SelectProperties> = ({
	data,
	value,
	setValue,
	label = '',
	description = '',
	error = false,
	disabled = false,
	errorMessage = '',
	warning = false,
	warningMessage = '',
	className = '',
	onBlur,
	startAdornment,
	onConfirm,
}) => {
	const [open, setOpen] = useState<boolean>(false);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleCloseButton = (event: MouseEvent<SVGSVGElement>) => {
		event.stopPropagation();
		setOpen(false);
	};

	return (
		<div className={className}>
			<FormControl variant="standard" style={{ width: '100%' }}>
				<div className="inputLabelRow" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					<Text
						className={`inputLabel ${label ? 'active' : ''}`}
						title={label}
						type="psm"
						fontWeight="medium"
						color={disabled ? TEXT.QUATERNARY : TEXT.PRIMARY}
					/>
					{warning && onConfirm && (
						<button type="button" className="confirmChip" onClick={onConfirm}>
							Confirm
						</button>
					)}
				</div>
				<Select
					startAdornment={startAdornment}
					open={open}
					value={value}
					SelectDisplayProps={{ 'aria-invalid': error || undefined }}
					onClose={handleClose}
					onOpen={handleOpen}
					onChange={setValue}
					input={<CustomizedInput error={error} warning={!error && warning} />}
					disabled={disabled}
					onBlur={onBlur}
					IconComponent={(props) => (
						<div {...props} style={{ right: 16, top: 8 }}>
							<ChevronDown />
						</div>
					)}
					MenuProps={{
						style: {
							maxWidth: '350px',
						},
					}}
				>
					{open && (
						<MenuItem style={{ position: 'absolute', top: 0, right: 0, zIndex: 1500 }}>
							<Close fontSize="small" onClick={handleCloseButton} />
						</MenuItem>
					)}
					{data && data.length > 0 ? (
						data.map((item, index) => (
							<MenuItem key={index} value={item.id} disabled={item.isDisabled}>
								<Text title={item.name ?? item.text ?? item.label ?? ''} type="psm" />
							</MenuItem>
						))
					) : (
						<MenuItem style={{ justifyContent: 'center' }}>
							<Text title="No Results" type="psm" fontWeight="semiBold" />
						</MenuItem>
					)}
				</Select>
				<Text
					className={`error ${error ? 'active' : ''}`}
					title={errorMessage}
					fontWeight="medium"
					type="psm"
					color={RED.BASE}
				/>
				{!error && (
					<Text
						className={`warning ${warning ? 'active' : ''}`}
						title={warningMessage}
						fontWeight="medium"
						type="psm"
						color={ORANGE.BASE}
					/>
				)}
				<Text
					className={`inputDescription ${!error && !warning && Boolean(description) ? 'active' : ''}`}
					title={description}
					type="psm"
					fontWeight="medium"
				/>
			</FormControl>
		</div>
	);
};

export default CustomSelect;
